import { ActionType, ComponentType } from '@cbhq/client-analytics';

import type { SDKFacematchType, Track } from '../../types';

import { addIdCaptureRumAction, addIdCaptureRumError } from './rum';

export type MediaPermissionKind = 'camera' | 'microphone';

/**
 * Coarse per-device outcome, emitted for debugging. `not_required` means the
 * device wasn't part of this facematch flow; `unknown` means we couldn't
 * determine it (browser can't pre-flight, or a combined denial we couldn't
 * attribute precisely).
 */
export type MediaPermissionStatus = 'granted' | 'denied' | 'not_required' | 'unknown';

export type PreflightResult = {
  status: 'granted' | 'blocked';
  /** Which device blocked the flow, when `status === 'blocked'`. */
  blockedKind?: MediaPermissionKind;
  /**
   * The `getUserMedia` DOMException name that caused the block (e.g.
   * `NotAllowedError`, `NotFoundError`). Fed to `CameraFailureView` so it can
   * pick the right copy.
   */
  errorName?: string;
  cameraStatus: MediaPermissionStatus;
  microphoneStatus: MediaPermissionStatus;
};

/**
 * The single place that enumerates the browser's video input devices. Throws if
 * the MediaDevices API is unavailable or `enumerateDevices` rejects — callers
 * decide how to treat that.
 */
async function getVideoInputDevices(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((device) => device.kind === 'videoinput');
}

/**
 * Fire-and-forget telemetry probe run early in every flow: reports when the
 * device has no camera (or the device list can't be read) so we can measure how
 * often users land in ID capture without a usable camera.
 */
export async function checkForCamera(track: Track): Promise<void> {
  try {
    const videoDevices = await getVideoInputDevices();

    if (videoDevices.length === 0) {
      track('camera_device_not_found', {
        componentType: ComponentType.content_script,
        action: ActionType.measurement,
        error: 'Camera device not found in the device list',
      });
      addIdCaptureRumAction('camera_device_not_found', {
        error: 'no_video_inputs',
      });
    }
  } catch (error) {
    track('camera_device_not_found', {
      componentType: ComponentType.content_script,
      action: ActionType.measurement,
      error: error instanceof Error ? error.message : 'Camera devices not accessible',
    });
    addIdCaptureRumAction('camera_device_not_found', {
      error: 'enumerate_failed',
    });
  }
}

/**
 * Gate for the legacy webcam-upload flow: resolves whether the device has a
 * usable camera. Assumes present when the MediaDevices API isn't supported (we
 * can't check, so let the capture step surface any issue).
 */
export async function isCameraPresent(track: Track): Promise<boolean> {
  function logError(error: string) {
    track('verify_document_video_input_error', {
      componentType: ComponentType.content_script,
      action: ActionType.measurement,
      error,
    });
    addIdCaptureRumError(new Error(error), {
      event: 'verify_document_video_input_error',
    });
  }

  if (!navigator.mediaDevices?.enumerateDevices) {
    logError('media_devices_api_not_supported');
    return true;
  }

  try {
    const videoInputs = await getVideoInputDevices();
    if (videoInputs.length === 0) {
      logError('no_video_inputs_available');
      return false;
    }
    return true;
  } catch (error) {
    logError(error instanceof Error ? error.message : 'enumerate_failed');
    return false;
  }
}

/**
 * Audio is only recorded for the Liveness and Motion facematch variants
 * (`video`, `video_only`, `motion`, `motion_only`). This is a property of the
 * capture *flow* (creation type), not the vendor — the plain photo facematch
 * (`standard`) and no-facematch (`none`) flows never record audio — so the
 * microphone requirement is derived from the facematch type alone.
 */
export function facematchRequiresMicrophone(facematchType: SDKFacematchType): boolean {
  return facematchType !== 'standard' && facematchType !== 'none';
}

function getErrorName(error: unknown): string {
  if (error instanceof DOMException) {
    return error.name;
  }
  if (error instanceof Error && error.name) {
    return error.name;
  }
  return 'UnknownError';
}

type DeviceProbe =
  /** Permission granted — the stream opened and was released. */
  | { outcome: 'granted' }
  /** Permission explicitly denied (`NotAllowedError` / `SecurityError`). */
  | { outcome: 'denied'; errorName: string }
  /** Non-permission failure (no device, hardware busy, …) — don't block. */
  | { outcome: 'unavailable'; errorName: string };

/**
 * Probe a single device with `getUserMedia` and release it immediately. Split
 * per-device (rather than one combined `{ video, audio }` request) so a denial
 * is attributed to the exact device deterministically — without relying on the
 * Permissions API, which Firefox / Safari don't support for camera/microphone.
 * The SDK re-acquires the devices (without re-prompting) once it launches.
 */
async function probeMediaDevice(constraints: MediaStreamConstraints): Promise<DeviceProbe> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    stream.getTracks().forEach((track) => track.stop());
    return { outcome: 'granted' };
  } catch (error) {
    const errorName = getErrorName(error);
    const isPermissionDenied = errorName === 'NotAllowedError' || errorName === 'SecurityError';
    return isPermissionDenied
      ? { outcome: 'denied', errorName }
      : { outcome: 'unavailable', errorName };
  }
}

/**
 * Pre-flight camera (and microphone for audio-recording facematch) access
 * before launching the vendor SDK. The SDKs reacquire the devices without
 * re-prompting once they launch, so this lets us short-circuit users who have
 * *explicitly denied* permission to a recovery screen — skipping a wasted IDV
 * session — while always reporting the resolved statuses.
 *
 * The camera is probed first, then the microphone (only when required), each as
 * a separate request so a denial is attributed to the exact device. A permission
 * denial (`NotAllowedError` / `SecurityError`) or a missing camera
 * (`NotFoundError`) blocks: this pre-flight only runs when cross-device is
 * disabled (the caller skips it when cross-device is forced), so there is no QR
 * fallback and these users would otherwise burn a session and fail inside the
 * SDK. Other transient device errors (e.g. `NotReadableError`, hardware busy)
 * are left to the SDK to surface.
 */
export async function preflightMediaPermissions({
  requiresMicrophone,
}: {
  requiresMicrophone: boolean;
}): Promise<PreflightResult> {
  const microphoneBaseline: MediaPermissionStatus = requiresMicrophone ? 'unknown' : 'not_required';

  // If the browser can't expose media devices we can't pre-flight anything —
  // assume access is available and let the SDK surface any issue, matching the
  // `isCameraPresent` fallback behaviour.
  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      status: 'granted',
      cameraStatus: 'unknown',
      microphoneStatus: microphoneBaseline,
    };
  }

  const camera = await probeMediaDevice({ video: true });
  if (camera.outcome === 'denied') {
    return {
      status: 'blocked',
      blockedKind: 'camera',
      errorName: camera.errorName,
      cameraStatus: 'denied',
      microphoneStatus: microphoneBaseline,
    };
  }

  // No camera at all. This pre-flight only runs when cross-device is disabled,
  // so there's no QR fallback — block and let CameraFailureView show its
  // "camera not available" copy rather than burn a session that fails in-SDK.
  if (camera.outcome === 'unavailable' && camera.errorName === 'NotFoundError') {
    return {
      status: 'blocked',
      blockedKind: 'camera',
      errorName: camera.errorName,
      cameraStatus: 'unknown',
      microphoneStatus: microphoneBaseline,
    };
  }

  // 'granted' -> we saw the device; a remaining 'unavailable' (transient error
  // such as hardware busy) -> 'unknown', left to the SDK to surface.
  const cameraStatus: MediaPermissionStatus = camera.outcome === 'granted' ? 'granted' : 'unknown';
  const cameraErrorName = camera.outcome === 'unavailable' ? camera.errorName : undefined;

  if (!requiresMicrophone) {
    return {
      status: 'granted',
      errorName: cameraErrorName,
      cameraStatus,
      microphoneStatus: 'not_required',
    };
  }

  const microphone = await probeMediaDevice({ audio: true });
  if (microphone.outcome === 'denied') {
    return {
      status: 'blocked',
      blockedKind: 'microphone',
      errorName: microphone.errorName,
      cameraStatus,
      microphoneStatus: 'denied',
    };
  }

  return {
    status: 'granted',
    errorName: microphone.outcome === 'unavailable' ? microphone.errorName : cameraErrorName,
    cameraStatus,
    microphoneStatus: microphone.outcome === 'granted' ? 'granted' : 'unknown',
  };
}
